import { useState } from "react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import type { PropertyRecord, HistoryEntry } from "../../types/portfolio";
import { calculateCAGR, calculateYoYGrowth, calculateTotalGrowth } from "../../lib/historySnapshot";
import { currency } from "../../lib/formatting";
import { InfoTooltip } from "../ui/InfoTooltip";
import { NumberInput } from "../ui/NumberInput";

interface Props {
  property: PropertyRecord;
  onUpdateProperty: (p: PropertyRecord) => void;
}

type SectionKey = "valueHistory" | "rentHistory";

export default function PropertyHistoryTab({ property, onUpdateProperty }: Props) {
  return (
    <div className="space-y-6">
      <div className="rounded-xl border border-neutral-200 bg-blue-50 px-4 py-3 text-sm text-neutral-700">
        Track how this property's value and rent have changed over time.
        Updates are captured automatically when you change <strong>Market Value</strong> or
        <strong> Weekly Rent</strong> — you can also add historical entries manually for
        backfill.
      </div>

      <HistorySection
        title="Value history"
        unit="dollar"
        sectionKey="valueHistory"
        entries={property.valueHistory ?? []}
        property={property}
        onUpdateProperty={onUpdateProperty}
      />

      <HistorySection
        title="Rent history (weekly)"
        unit="dollar"
        sectionKey="rentHistory"
        entries={property.rentHistory ?? []}
        property={property}
        onUpdateProperty={onUpdateProperty}
      />
    </div>
  );
}

interface SectionProps {
  title: string;
  unit: "dollar";
  sectionKey: SectionKey;
  entries: HistoryEntry[];
  property: PropertyRecord;
  onUpdateProperty: (p: PropertyRecord) => void;
}

function HistorySection({ title, sectionKey, entries, property, onUpdateProperty }: SectionProps) {
  const [adding, setAdding] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);

  const sorted = [...entries].sort((a, b) => a.date.localeCompare(b.date));
  const latest = sorted[sorted.length - 1];
  const cagr = calculateCAGR(entries);
  const yoy = calculateYoYGrowth(entries);
  const totalGrowth = calculateTotalGrowth(entries);

  function persist(next: HistoryEntry[]) {
    onUpdateProperty({ ...property, [sectionKey]: next });
  }

  function addEntry(date: string, value: number) {
    if (!date || !value || value <= 0) return;
    const next = [...entries.filter((e) => e.date !== date), { id: crypto.randomUUID(), date, value }];
    persist(next);
    setAdding(false);
  }

  function updateEntry(id: string, date: string, value: number) {
    const next = entries.map((e) => e.id === id ? { ...e, date, value } : e);
    persist(next);
    setEditId(null);
  }

  function deleteEntry(id: string) {
    persist(entries.filter((e) => e.id !== id));
  }

  return (
    <div className="rounded-2xl border border-neutral-200 bg-white p-5">
      <div className="mb-4 flex items-baseline justify-between gap-3">
        <h3 className="text-base font-semibold text-neutral-900">{title}</h3>
        <button
          onClick={() => setAdding(true)}
          className="rounded-lg border border-neutral-200 px-3 py-1.5 text-xs font-medium text-neutral-700 hover:bg-neutral-50 transition-colors"
        >
          + Add historical entry
        </button>
      </div>

      {entries.length === 0 ? (
        <p className="text-sm text-neutral-500">
          No history yet — values will be auto-captured when you update {sectionKey === "valueHistory" ? "Market Value" : "Weekly Rent"}.
          You can also add historical entries manually.
        </p>
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
            <Metric label="Latest" value={latest ? currency(latest.value) : "—"} />
            <Metric
              label={<>Total growth <InfoTooltip tip="Percentage change from earliest entry to latest." /></>}
              value={totalGrowth !== null ? `${(totalGrowth * 100).toFixed(1)}%` : "—"}
              positive={totalGrowth !== null && totalGrowth > 0}
              negative={totalGrowth !== null && totalGrowth < 0}
            />
            <Metric
              label={<>CAGR <InfoTooltip tip="Compound Annual Growth Rate — the steady annual % growth that would produce the same final value over this period." /></>}
              value={cagr !== null ? `${(cagr * 100).toFixed(1)}%` : "—"}
              positive={cagr !== null && cagr > 0}
              negative={cagr !== null && cagr < 0}
            />
            <Metric
              label={<>YoY <InfoTooltip tip="Year-over-year change — % change between the latest entry and the closest entry from ~12 months ago." /></>}
              value={yoy !== null ? `${(yoy * 100).toFixed(1)}%` : "—"}
              positive={yoy !== null && yoy > 0}
              negative={yoy !== null && yoy < 0}
            />
          </div>

          <div className="h-64 mb-4">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={sorted}>
                <CartesianGrid stroke="#f3f4f6" />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => currency(Number(v))} width={80} />
                <Tooltip
                  formatter={(v) => currency(Number(v))}
                  labelStyle={{ fontSize: 12 }}
                  contentStyle={{ fontSize: 12 }}
                />
                <Line type="monotone" dataKey="value" stroke="#0ea5e9" strokeWidth={2} dot={{ r: 4 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-neutral-500 uppercase tracking-wide">
                  <th className="pb-2">Date</th>
                  <th className="pb-2 text-right">Value</th>
                  <th className="pb-2 w-32"></th>
                </tr>
              </thead>
              <tbody>
                {sorted.map((entry) => (
                  editId === entry.id ? (
                    <EditRow
                      key={entry.id}
                      entry={entry}
                      onSave={(d, v) => updateEntry(entry.id, d, v)}
                      onCancel={() => setEditId(null)}
                    />
                  ) : (
                    <tr key={entry.id} className="border-t border-neutral-100">
                      <td className="py-2">{entry.date}</td>
                      <td className="py-2 text-right tabular-nums">{currency(entry.value)}</td>
                      <td className="py-2 text-right">
                        <button onClick={() => setEditId(entry.id)} className="text-xs text-blue-600 hover:underline mr-2">Edit</button>
                        <button onClick={() => deleteEntry(entry.id)} className="text-xs text-red-600 hover:underline">Delete</button>
                      </td>
                    </tr>
                  )
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {adding && (
        <AddEntryModal
          onSave={addEntry}
          onCancel={() => setAdding(false)}
          existingDates={new Set(entries.map((e) => e.date))}
        />
      )}
    </div>
  );
}

function Metric({ label, value, positive, negative }: { label: React.ReactNode; value: string; positive?: boolean; negative?: boolean }) {
  const colorClass = positive ? "text-emerald-700" : negative ? "text-red-700" : "text-neutral-900";
  return (
    <div className="rounded-xl border border-neutral-200 px-3 py-2 bg-neutral-50">
      <div className="text-xs text-neutral-500 flex items-center gap-1">{label}</div>
      <div className={`text-lg font-semibold tabular-nums ${colorClass}`}>{value}</div>
    </div>
  );
}

function EditRow({ entry, onSave, onCancel }: { entry: HistoryEntry; onSave: (date: string, value: number) => void; onCancel: () => void }) {
  const [date, setDate] = useState(entry.date);
  const [value, setValue] = useState(String(entry.value));

  return (
    <tr className="border-t border-neutral-100 bg-blue-50">
      <td className="py-2">
        <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="rounded-lg border border-neutral-200 px-2 py-1 text-sm" />
      </td>
      <td className="py-2 text-right">
        <NumberInput value={value} onChange={(e) => setValue(e.target.value)} className="rounded-lg border border-neutral-200 px-2 py-1 text-sm text-right w-32" />
      </td>
      <td className="py-2 text-right">
        <button onClick={() => onSave(date, Number(value))} className="text-xs text-blue-600 hover:underline mr-2">Save</button>
        <button onClick={onCancel} className="text-xs text-neutral-500 hover:underline">Cancel</button>
      </td>
    </tr>
  );
}

function AddEntryModal({ onSave, onCancel, existingDates }: { onSave: (date: string, value: number) => void; onCancel: () => void; existingDates: Set<string> }) {
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [value, setValue] = useState("");
  const conflict = existingDates.has(date);
  const valid = date && Number(value) > 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4" onClick={onCancel}>
      <div className="w-full max-w-sm rounded-2xl border border-neutral-200 bg-white p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-base font-semibold text-neutral-900 mb-4">Add historical entry</h3>
        <div className="space-y-3">
          <div>
            <label className="block text-xs font-medium text-neutral-700 mb-1">Date</label>
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="w-full rounded-lg border border-neutral-200 px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="block text-xs font-medium text-neutral-700 mb-1">Value</label>
            <NumberInput value={value} onChange={(e) => setValue(e.target.value)} placeholder="e.g. 600000" className="w-full rounded-lg border border-neutral-200 px-3 py-2 text-sm" autoFocus />
          </div>
          {conflict && (
            <p className="text-xs text-amber-700 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2">
              An entry already exists for this date — saving will replace it.
            </p>
          )}
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <button onClick={onCancel} className="rounded-lg px-3 py-1.5 text-sm text-neutral-600 hover:bg-neutral-100 transition-colors">Cancel</button>
          <button
            onClick={() => onSave(date, Number(value))}
            disabled={!valid}
            className="rounded-lg bg-neutral-900 px-3 py-1.5 text-sm font-medium text-white hover:bg-neutral-700 disabled:opacity-50 transition-colors"
          >
            Add
          </button>
        </div>
      </div>
    </div>
  );
}
